/**
 * Sprint 7 — Real /today page backed by GET /api/today.
 *
 * Replaces the mock-only `TodayPage`. Mock-rich strips (live signals,
 * recent content, brain learnings, today's agenda) are kept off this
 * page on purpose — they're Sprint 8+ work. We surface them via a
 * "more coming next sprint" footer so the empty space reads
 * intentional, not broken.
 */
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Sparkles, CheckCircle2, Edit3, MessageCircle, Linkedin, Mail, Send, Clock, ArrowRight, RefreshCw, MailQuestion, BellRing, UserRound, Radar } from 'lucide-react';
import { todayApi, authApi } from "@/lib/api";
import { useAssistantName } from "@/hooks/useAssistantName";
import { personalizeAssistantText } from "@/lib/personaSamples";
import { TODAY_EXTENDED_FEATURES_ENABLED } from "@/lib/featureFlags";
import { pulseTileNavHandler } from "@/pages/today/todayPulseNav";

const PULSE_LOADING_KEYS = ["pulse-skel-a", "pulse-skel-b", "pulse-skel-c", "pulse-skel-d", "pulse-skel-e"];

const CHANNEL_META = {
  email:    { Icon: Mail,          ring: "text-rose-700 bg-rose-50 ring-rose-200" },
  whatsapp: { Icon: MessageCircle, ring: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
  telegram: { Icon: Send,          ring: "text-sky-700 bg-sky-50 ring-sky-200" },
  linkedin: { Icon: Linkedin,      ring: "text-blue-700 bg-blue-50 ring-blue-200" },
  sms:      { Icon: MessageCircle, ring: "text-amber-700 bg-amber-50 ring-amber-200" },
};

const KIND_META = {
  draft_approval: {
    label: "Draft awaiting approval",
    badge: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    Icon: Sparkles,
  },
  new_inbound: {
    label: "New message",
    badge: "bg-sky-50 text-sky-700 ring-sky-200",
    Icon: MessageCircle,
  },
  reply_needed: {
    label: "Reply needed",
    badge: "bg-amber-50 text-amber-700 ring-amber-200",
    Icon: MailQuestion,
  },
  follow_up: {
    label: "Suggested follow-up",
    badge: "bg-slate-100 text-slate-700 ring-slate-200",
    Icon: BellRing,
  },
  signal_engagement: {
    label: "Community signal",
    badge: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
    Icon: Radar,
  },
};

function fmtAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function greetForHour(hour) {
  if (hour < 5) return "Good night";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function DecisionActions({ d, nav }) {
  if (d.kind === "draft_approval") {
    return (
      <>
        <Button size="sm" className="h-9 sm:h-7 text-[11.5px]" onClick={() => nav(d.action_path)} data-testid={`today-review-${d.id}`}>
          <CheckCircle2 size={12} /> Review &amp; approve
        </Button>
        <Button size="sm" variant="outline" className="h-9 sm:h-7 text-[11.5px]" onClick={() => nav(d.action_path)}>
          <Edit3 size={12} /> Edit
        </Button>
      </>
    );
  }
  if (d.kind === "signal_engagement") {
    return (
      <>
        <Button size="sm" className="h-9 sm:h-7 text-[11.5px]" onClick={() => nav("/signals")} data-testid={`today-open-${d.id}`}>
          <Radar size={12} /> Review signal
        </Button>
        {d.external_url && (
          <Button size="sm" variant="outline" className="h-9 sm:h-7 text-[11.5px]"
            onClick={() => window.open(d.external_url, "_blank", "noopener,noreferrer")}>
            <ArrowRight size={12} /> Open in {d.channel_label}
          </Button>
        )}
        {d.community && (
          <span className="text-[10.5px] text-slate-400">· {d.community}</span>
        )}
      </>
    );
  }
  return (
    <Button size="sm" className="h-9 sm:h-7 text-[11.5px]" onClick={() => nav(d.action_path)} data-testid={`today-open-${d.id}`}>
      <ArrowRight size={12} /> Open conversation
    </Button>
  );
}

DecisionActions.propTypes = {
  d: PropTypes.shape({
    id: PropTypes.string,
    kind: PropTypes.string,
    action_path: PropTypes.string,
    external_url: PropTypes.string,
    channel_label: PropTypes.string,
    community: PropTypes.string,
  }).isRequired,
  nav: PropTypes.func.isRequired,
};

function DecisionCard({ d, nav, maya }) {
  const cm = CHANNEL_META[d.channel] || CHANNEL_META.email;
  const km = KIND_META[d.kind] || KIND_META.reply_needed;
  return (
    <article
      data-testid={`today-decision-${d.id}`}
      className="rounded-xl border border-slate-200 bg-white px-3 sm:px-5 py-3.5 sm:py-4 transition-colors hover:border-indigo-200 hover:shadow-[0_2px_12px_-2px_rgba(99,102,241,0.10)]"
    >
      <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
        <span className={cn("mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded-lg ring-1 shrink-0", cm.ring)}>
          <cm.Icon size={13} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start sm:items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-slate-900 break-words">{d.from}</span>
            <span className="text-[10.5px] text-slate-400">· on {d.channel_label}</span>
            <span className={cn("sm:ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1", km.badge)}>
              <km.Icon size={10} className="shrink-0" /> {km.label}
            </span>
          </div>
          <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-slate-700 leading-relaxed line-clamp-3 sm:line-clamp-2 break-words">{personalizeAssistantText(d.context, maya)}</p>
          {d.suggestion && (
            <div className="mt-2.5 rounded-lg bg-slate-50/80 ring-1 ring-slate-200 px-3 py-2 min-w-0">
              <div className="flex flex-wrap items-baseline gap-1.5 text-[10.5px] uppercase tracking-wider text-indigo-600 font-semibold">
                <span className="inline-flex items-center gap-1"><Sparkles size={9} /> {maya} suggests</span>
                {d.confidence > 0 && (
                  <span className="sm:ml-auto text-slate-500 normal-case tracking-normal">confidence {d.confidence}%</span>
                )}
              </div>
              <p className="mt-1 text-[12.5px] text-slate-700 leading-relaxed line-clamp-3 break-words">{personalizeAssistantText(d.suggestion, maya)}</p>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <DecisionActions d={d} nav={nav} />
            {d.last_message_at && (
              <span className="w-full sm:w-auto sm:ml-auto inline-flex items-center gap-1 text-[10.5px] text-slate-400">
                <Clock size={10} /> {fmtAgo(d.last_message_at)}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

DecisionCard.propTypes = {
  d: PropTypes.shape({
    id: PropTypes.string,
    channel: PropTypes.string,
    kind: PropTypes.string,
    from: PropTypes.string,
    channel_label: PropTypes.string,
    context: PropTypes.string,
    suggestion: PropTypes.string,
    confidence: PropTypes.number,
    action_path: PropTypes.string,
    external_url: PropTypes.string,
    community: PropTypes.string,
    last_message_at: PropTypes.string,
  }).isRequired,
  nav: PropTypes.func.isRequired,
  maya: PropTypes.string,
};

function PulseTile({ p, onClick }) {
  const cls = "rounded-lg bg-slate-50/60 px-3 py-3 text-left w-full transition-colors min-h-[76px]";
  const interactive = onClick ? " hover:bg-slate-100 cursor-pointer" : "";
  const inner = (
    <>
      <div className="text-[20px] sm:text-[22px] font-semibold tabular-nums text-slate-900 leading-none">{p.v}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium break-words">{p.k}</div>
      <div className="text-[10.5px] text-slate-500 break-words">{p.sub}</div>
    </>
  );
  const tid = `today-pulse-${p.k.toLowerCase().replace(/\W+/g, "-")}`;
  return onClick
    ? <button type="button" onClick={onClick} className={cls + interactive} data-testid={tid}>{inner}</button>
    : <div className={cls} data-testid={tid}>{inner}</div>;
}

PulseTile.propTypes = {
  p: PropTypes.shape({
    k: PropTypes.string.isRequired,
    v: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sub: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
};

export default function BackendTodayPage() {
  const nav = useNavigate();
  const maya = useAssistantName();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [me, setMe] = useState(null);

  async function load() {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [data, who] = await Promise.all([
        todayApi.get(),
        authApi.me().catch(() => null),
      ]);
      setState({ loading: false, error: null, data });
      if (who) setMe(who);
    } catch (e) {
      setState({ loading: false, error: e.message || "Failed to load", data: null });
    }
  }

  useEffect(() => { queueMicrotask(load); }, []);

  const now = new Date();
  const greet = greetForHour(now.getHours());
  const firstName = useMemo(() => {
    const dn = (me?.display_name || me?.email || "").trim();
    return (dn.split(/\s|@/)[0] || "there");
  }, [me]);

  const data = state.data;
  const decisions = data?.decisions || [];
  const pulseTiles = (data?.pulse || []).filter((p) => TODAY_EXTENDED_FEATURES_ENABLED || p.k !== "Window");
  const pulseSkeletonCount = TODAY_EXTENDED_FEATURES_ENABLED ? 5 : 4;

  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0 h-full overflow-y-auto overflow-x-hidden scrollbar-thin bg-white" data-testid="today-page">
      <div className="max-w-[960px] w-full mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10 md:py-12 space-y-8 sm:space-y-10">

        {/* Hero */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <h1 className="text-[28px] sm:text-[36px] md:text-[44px] lg:text-[48px] leading-[1.1] sm:leading-[1.05] font-semibold tracking-tight text-slate-900 break-words">
              {greet}, {firstName}.
            </h1>
            <p className="mt-2 sm:mt-3 text-[14px] sm:text-[15px] text-slate-600 max-w-[640px] leading-relaxed">
              Here&apos;s what <span className="font-semibold text-indigo-700">{maya}</span> is watching and what needs you today.
            </p>
          </div>
          <div className="flex items-center justify-between sm:block sm:text-right shrink-0 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                {now.toLocaleDateString(undefined, { weekday: "long" })}
              </div>
              <div className="text-[13px] sm:text-[14px] text-slate-700 tabular-nums">
                {now.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-9 sm:h-7 text-[11.5px] text-slate-500 sm:mt-2" onClick={load} disabled={state.loading} data-testid="today-refresh">
              <RefreshCw size={11} className={cn(state.loading && "animate-spin")} /> Refresh
            </Button>
          </div>
        </header>

        {/* Overnight summary */}
        <section
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/50 via-white to-violet-50/30 px-4 sm:px-7 py-5 sm:py-6"
          data-testid="today-overnight"
        >
          <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-semibold inline-flex items-center gap-1.5">
            <Sparkles size={12} /> {maya} · last {data?.overnight?.window_hours ?? 12}h
          </div>
          <OvernightSummaryBody
            loading={state.loading}
            error={state.error}
            data={data}
            decisionsCount={decisions.length}
          />
        </section>

        {/* Needs your decision */}
        <section className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-tight text-slate-900">Needs your decision</h2>
            <span className="text-[11.5px] text-slate-500 shrink-0">
              {decisions.length} {decisions.length === 1 ? "item" : "items"}
            </span>
          </div>
          <div className="space-y-2.5" data-testid="today-decisions">
            {state.loading && ["dec-a", "dec-b", "dec-c"].map((key) => (
              <Skeleton key={key} className="h-[92px] rounded-xl" />
            ))}
            {!state.loading && decisions.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 sm:px-5 py-8 text-center" data-testid="today-empty-decisions">
                <UserRound size={20} className="mx-auto text-slate-400" />
                <div className="mt-2 text-[13.5px] font-semibold text-slate-900">All caught up</div>
                <p className="mt-1 text-[12px] text-slate-500 leading-relaxed max-w-[340px] mx-auto">
                  Nothing needs your attention right now. New drafts and follow-up suggestions will show up here as they come in.
                </p>
                <Button size="sm" variant="outline" className="h-9 sm:h-7 text-[11.5px] mt-4" onClick={() => nav("/inbox")}>
                  Go to inbox <ArrowRight size={11} />
                </Button>
              </div>
            )}
            {!state.loading && decisions.map((d) => (
              <DecisionCard key={d.id} d={d} nav={nav} maya={maya} />
            ))}
          </div>
        </section>

        {/* Pulse */}
        <section className="min-w-0">
          <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-tight text-slate-900 mb-3">Pulse</h2>
          <div className={cn("grid grid-cols-2 gap-2 sm:gap-3", TODAY_EXTENDED_FEATURES_ENABLED ? "md:grid-cols-5" : "md:grid-cols-4")} data-testid="today-pulse">
            {state.loading
              ? PULSE_LOADING_KEYS.slice(0, pulseSkeletonCount).map((key) => <Skeleton key={key} className="h-[76px] rounded-lg" />)
              : pulseTiles.map((p) => (
                  <PulseTile key={p.k} p={p} onClick={pulseTileNavHandler(p.k, nav)} />
                ))
            }
          </div>
        </section>

        {TODAY_EXTENDED_FEATURES_ENABLED && (
        <section className="border-t border-slate-100 pt-6 sm:pt-8">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Coming next</h3>
          <p className="text-[12.5px] text-slate-500 leading-relaxed max-w-[560px]">
            Live signals, today&apos;s agenda, recent content drafts, and {maya}&apos;s overnight learning notes light up in <span className="font-semibold text-slate-700">Sprint 8</span> once the researcher and signals agent are live.
          </p>
        </section>
        )}

      </div>
    </div>
  );
}

function Chip({ value, label }) {
  return (
    <span className="inline-flex max-w-full flex-wrap items-baseline gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 my-0.5 text-indigo-700 ring-1 ring-indigo-200 align-middle text-[0.85em] leading-snug">
      <span className="font-bold tabular-nums">{value ?? 0}</span>
      <span className="font-medium">{label}</span>
    </span>
  );
}

Chip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
};

function OvernightSummaryBody({ loading, error, data, decisionsCount }) {
  if (loading) return <Skeleton className="mt-3 h-6 w-3/4" />;
  if (error) {
    return (
      <p className="mt-2.5 text-[14px] text-rose-700 break-words">
        Couldn&apos;t reach the backend: {error}
      </p>
    );
  }
  return (
    <p className="mt-2.5 text-[15px] sm:text-[18px] leading-[1.85] sm:leading-[1.9] text-slate-800 [overflow-wrap:anywhere]">
      I handled <Chip value={data?.overnight?.replies} label="sent replies" />, drafted{" "}
      <Chip value={data?.overnight?.drafts} label="messages waiting for your sign-off" />, and saw{" "}
      <Chip value={data?.overnight?.inbound} label="new inbound messages" /> across your channels.
      {decisionsCount > 0 && (
        <> A few of them need a decision below.</>
      )}
    </p>
  );
}

OvernightSummaryBody.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  data: PropTypes.object,
  decisionsCount: PropTypes.number,
};
